import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import {
  RouteTabGroupComponent,
  TabGroupConfig,
} from '@features/tab-group/route-tab-group.component';

import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';
import { SitesStore } from '@models/sites/sites.store';

import { ROUTING_PATH } from '@shared/constants/routingPath';
import { OverlayService } from '@shared/services/overlay.service';
import { MainStore } from '@shared/store/main/main.store';
import { OrganizationService } from '@shared/store/organization/organization.service';

import { SiteHeaderComponent } from './components/site-header.components/site-header.components';
import { getSiteWorkflow } from './directives/site-workflow.directive';

@Component({
  imports: [SiteHeaderComponent, AsyncPipe, RouteTabGroupComponent, RouterOutlet],
  selector: 'aux-site',
  templateUrl: './site.component.html',
})
export class SiteComponentComponent implements OnDestroy, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly mainStore = inject(MainStore);
  private readonly overlayService = inject(OverlayService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly sitesQuery = inject(SitesQuery);
  private readonly sitesService = inject(SitesService);
  private readonly sitesStore = inject(SitesStore);
  private readonly vendorService = inject(OrganizationService);

  private siteId$ = new BehaviorSubject('');

  site$ = combineLatest([this.siteId$, this.sitesQuery.selectAll()]).pipe(
    filter(([siteId]) => !!siteId),
    tap(([siteId]) => this.sitesStore.setActive([siteId])),
    map(([siteId]) => this.sitesQuery.getEntity(siteId))
  );

  siteWorkflow = getSiteWorkflow();
  tabs: TabGroupConfig[] = [
    { label: 'Site Details', route: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.DETAILS },
    { label: 'Site Costs', route: ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.COSTS },
  ];

  ngOnInit(): void {
    combineLatest([this.sitesQuery.selectAll(), this.sitesQuery.selectLoading(), this.siteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([, loadingSites, siteId]) => {
        if (!this.sitesQuery.hasEntity(siteId) && !loadingSites) {
          this.goToSiteList();
        }
      });
  }

  ngOnDestroy(): void {
    this.mainStore.update({ fullPage: false });
    this.sitesStore.setActive(['']);
  }

  constructor() {
    this.mainStore.update({ fullPage: true });

    this.route?.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.siteId$.next(params.id ?? '');
    });

    this.sitesService.get().pipe(takeUntilDestroyed()).subscribe();
    this.vendorService.listIdOrganizations$().pipe(takeUntilDestroyed()).subscribe();
  }

  goToSiteList() {
    void this.router.navigate([
      ROUTING_PATH.INVESTIGATOR.INDEX,
      ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX,
      ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX,
    ]);
  }

  indexFn = (tabs: TabGroupConfig[], activeRoute: string) => {
    let index = 0;

    tabs.forEach((tab, i) => {
      if (activeRoute.includes(`/${tab.route}`)) {
        index = i;
      }
    });

    return index;
  };
}
